import { createFileRoute, Link as RouterLink } from "@tanstack/react-router";
import { Building2, Plus } from "lucide-react";
import {
  Banner,
  Button,
  EmptyState,
  ListSkeleton,
  PageHeader,
  RelativeTime,
  SearchField,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ICON_STROKE,
} from "@optymyse/suite-ui";
import type { CompanyRow } from "../../shared/types/directory";
import { edits } from "../../shared/schemas/workspaces";
import { useCompanies } from "../lib/directory";
import { arrange, columnSort, sortFrom, sortParam, type Column, type ListSort } from "../lib/listing";
import { workspaceSlug } from "../lib/hosts";
import { useWorkspace } from "../lib/workspaces";
import { fill, strings } from "../lib/strings";
import { ApiError } from "../lib/api";
import { DirectoryProblem, Page } from "../components/directory/Page";

/** No country: Link's pages hold nothing of a company's address, only its record does. */
const COLUMNS = ["name", "domain", "updated"] as const;
type CustomerColumn = (typeof COLUMNS)[number];

const BY: Readonly<Record<CustomerColumn, Column<CompanyRow>>> = {
  name: { kind: "name", value: (row) => row.display_name },
  domain: { kind: "name", value: (row) => row.domain },
  updated: { kind: "time", value: (row) => row.updated_at },
};

type CustomersSearch = { q?: string; sort?: string };

/**
 * The customers: the organisation's companies at Link, A to Z by name, with the suite's search above them ("/" comes
 * to it, Esc clears it) and each column's name sorting by it; the address keeps both (SuiteUi's README, "Lists").
 * The page holds the whole list and sorts and searches it (src/client/lib/listing.ts).
 */
function CustomersPage() {
  const { q = "", sort: sortText } = Route.useSearch();
  const navigate = Route.useNavigate();
  const view = useWorkspace(workspaceSlug());
  const companies = useCompanies();
  const sort = sortFrom(sortText, COLUMNS, "name");
  const mayEdit = view.data ? edits(view.data.role) : false;
  const search = (next: { q?: string; sort?: ListSort<CustomerColumn> }) =>
    void navigate({
      search: (was) => {
        const nextQ = next.q ?? was.q ?? "";
        const nextSort = next.sort ? sortParam(next.sort, "name") : was.sort;
        return { ...(nextQ ? { q: nextQ } : {}), ...(nextSort ? { sort: nextSort } : {}) };
      },
      replace: true,
    });
  const all = companies.data?.items ?? [];
  const rows = arrange(all, {
    q,
    sort,
    columns: BY,
    tiebreak: "name",
    searched: (row) => [row.display_name, row.legal_name, row.domain],
  });

  return (
    <Page wide>
      <PageHeader
        title={strings.customers.title}
        description={strings.customers.description}
        actions={
          mayEdit ? (
            <Button asChild variant="primary">
              <RouterLink to="/admin/customers/new">
                <Plus aria-hidden strokeWidth={ICON_STROKE} />
                {strings.customers.add}
              </RouterLink>
            </Button>
          ) : undefined
        }
      />
      <div role="search" aria-label={strings.customers.title} className="flex flex-wrap items-center gap-3">
        <SearchField value={q} onSearch={(next) => search({ q: next })} label={strings.customers.search} delay={0} />
        {companies.data && (
          <p className="text-secondary text-ink-muted" aria-live="polite">
            {q
              ? `${rows.length} of ${fill(strings.customers.count, { count: all.length })}`
              : fill(strings.customers.count, { count: all.length })}
          </p>
        )}
      </div>
      {companies.data?.capped && (
        <Banner tone="warning">{fill(strings.directory.capped, { cap: companies.data.cap })}</Banner>
      )}
      {companies.isPending ? (
        <ListSkeleton rows={6} lines={1} label={strings.customers.title} />
      ) : companies.isError ? (
        <DirectoryProblem
          {...(companies.error instanceof ApiError ? { message: companies.error.message } : {})}
          onRetry={() => void companies.refetch()}
        />
      ) : all.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={strings.customers.empty}
          titleAs="h2"
          action={
            mayEdit ? (
              <Button asChild variant="primary">
                <RouterLink to="/admin/customers/new">{strings.customers.add}</RouterLink>
              </Button>
            ) : undefined
          }
        >
          {strings.customers.emptyBody}
        </EmptyState>
      ) : (
        <Table>
          <TableCaption>{strings.customers.title}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead sort={columnSort(sort, "name", (next) => search({ sort: next }))}>
                {strings.customers.columns.name}
              </TableHead>
              <TableHead sort={columnSort(sort, "domain", (next) => search({ sort: next }))}>
                {strings.customers.columns.domain}
              </TableHead>
              <TableHead sort={columnSort(sort, "updated", (next) => search({ sort: next }))}>
                {strings.customers.columns.updated}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} className="text-ink-muted">
                  {fill(strings.customers.noMatches, { q })}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <RouterLink
                      to="/admin/customers/$companyId"
                      params={{ companyId: row.id }}
                      className="font-medium text-ink underline-offset-2 hover:underline"
                    >
                      {row.display_name}
                    </RouterLink>
                    {row.legal_name && row.legal_name !== row.display_name && (
                      <span className="block text-secondary text-ink-muted">{row.legal_name}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-ink-muted">{row.domain ?? ""}</TableCell>
                  <TableCell className="whitespace-nowrap text-ink-muted">
                    <RelativeTime at={row.updated_at} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </Page>
  );
}

export const Route = createFileRoute("/admin/customers/")({
  validateSearch: (search: Record<string, unknown>): CustomersSearch => ({
    ...(typeof search.q === "string" && search.q !== "" ? { q: search.q } : {}),
    ...(typeof search.sort === "string" && search.sort !== "" ? { sort: search.sort } : {}),
  }),
  component: CustomersPage,
});
